// Third-party
import React, { Component } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import Downshift from 'downshift';
import uniqueId from 'lodash/uniqueId';
import * as ProposalNS from '../../Proposal';

// Components
import { InfoTooltip } from '@/views/components/tooltips';
import { Input, EditableInput, DateRangeSelect, Checkbox, SelectOption, SelectInput, DateQuickRangeOneWay } from '@/views/components/inputs';

// Utils
import { formatMessageWithLocale, FormattedMessage, formatNumber, DEFAULT_DECIMALS, generateLocalizedString } from '@/utils/i18n';
import { REBALANCING_PERIODS } from '@/utils/portfolio/utils';
import { CHART_STARTING_CAPITAL, PERIOD_MONTHS, TIME_PERIODS } from '@/utils/constants';
import { getDefaultToolbarOption, monthsToPeriodFromValues } from '@/utils/charts';
import { periodToMonths } from '@/utils/time';

import Nullable from '@/types/nullable';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import LocalizedString from '@/types/localized-string';
import Locale from '@/types/locale';
import { getDateQuickRangeFromToolbarConfig } from '@/utils/time-range-presets';
import { getMaxEndDate } from '@/utils/dates';

const DEFAULT_OPTIONS = {
  capital: CHART_STARTING_CAPITAL,
};

type Props = {
  className?: string;
  proposal: ProposalNS.Proposal;
  toolbarOptionConfig?: ProposalNS.ToolbarOptionConfig;
  setToolbarOption: (key: string, option: ProposalToolbarOption, comparisonToolbarKey?: string) => void;
  toolbarOption?: ProposalToolbarOption;
  sectionIteratorIndex?: number;
};

type State = {
  initialized: boolean;
};

class ProposalToolbar extends Component<Props> {
  readonly state: State = {
    initialized: false,
  };

  private readonly useMonthlyLabelId: string;

  constructor(props: Props) {
    super(props);

    // Generate unique ID for this instance to avoid collisions when multiple toolbars are rendered
    this.useMonthlyLabelId = uniqueId('proposal-toolbar-use-monthly-');

    const { proposal, toolbarOptionConfig } = this.props;

    if (!toolbarOptionConfig) {
      return;
    }

    const { key, comparisonToolbarKey, defaultValues, dateQuickRangePreset, dateQuickRangeValues } = toolbarOptionConfig;

    const periods = getDateQuickRangeFromToolbarConfig(toolbarOptionConfig, proposal);

    /*
    dateQuickRangeValues
    */
    const defaultOptions = proposal.toolbarOptions[key] || getDefaultToolbarOption(proposal, comparisonToolbarKey, defaultValues, periods);

    this.setToolbarOption(defaultOptions);
    this.state = { initialized: true };
  }

  getRebalancingOptions = () => {
    const {
      proposal: {
        options: { locale },
      },
    } = this.props;

    return [
      { label: formatMessageWithLocale('Charts.Growth.RebalanceOptions.Daily', locale), value: REBALANCING_PERIODS.DAILY.value },
      { label: formatMessageWithLocale('Charts.Growth.RebalanceOptions.Quarterly', locale), value: REBALANCING_PERIODS.QUARTERLY.value },
      { label: formatMessageWithLocale('Charts.Growth.RebalanceOptions.Semiannually', locale), value: REBALANCING_PERIODS.SEMIANNUALLY.value },
      { label: formatMessageWithLocale('Charts.Growth.RebalanceOptions.Yearly', locale), value: REBALANCING_PERIODS.YEARLY.value },
    ];
  };

  setToolbarOption = <K extends keyof ProposalToolbarOption>(option: Pick<ProposalToolbarOption, K>) => {
    const { toolbarOptionConfig } = this.props;
    if (!toolbarOptionConfig) {
      return;
    }

    this.props.setToolbarOption(toolbarOptionConfig.key, option, toolbarOptionConfig?.comparisonToolbarKey);
  };

  onChangeDate = (startDate: Nullable<Date>, endDate: Date, period?: TIME_PERIODS, periodLabel?: LocalizedString, monthsRange?: Nullable<number>) => {
    // Subtract 1 day to not spill over next month
    const newEndDate = moment.min(moment(endDate).endOf('month').subtract(1, 'days'), moment()).toDate();

    this.setToolbarOption({ startDate, endDate: newEndDate, period, periodLabel, monthsRange });
  };

  // DateQuickRange
  onMonthsRangeChange = (period: TIME_PERIODS) => {
    const monthsRange = periodToMonths(period);
    const endDate = getMaxEndDate(this.props.proposal.monthEndDataOption);
    const startDate =
      period === TIME_PERIODS.CI || period === TIME_PERIODS.INC ? null : moment(endDate).startOf('month').subtract(monthsRange, 'months').toDate();
    const periodLabel = generateLocalizedString(`Common.Periods.${period}`);
    this.onChangeDate(startDate, endDate, period, periodLabel, monthsRange);
  };

  onChangeTargetROI = (value: string) => {
    const targetROI = value ? Number(value) : undefined;

    this.setToolbarOption({ targetROI });
  };

  onCapitalChange = (value: string) => {
    const capital = value ? Number(value) : 0;
    this.setToolbarOption({ capital });
  };

  onBlurCapitalChange = () => {
    const capital = this.props.toolbarOption !== undefined ? this.props.toolbarOption.capital : undefined;
    if (capital === undefined) {
      this.setToolbarOption({ capital: DEFAULT_OPTIONS.capital });
    }
  };

  onIntervalChange = () => {
    const useMonthly = this.props.toolbarOption !== undefined ? this.props.toolbarOption.useMonthly : undefined;
    this.setToolbarOption({ useMonthly: !useMonthly });
  };

  onRebalanceChange = (value: string) => {
    const rebalancePeriod = REBALANCING_PERIODS[value.toUpperCase()];
    this.setToolbarOption({ rebalancePeriod });
  };

  renderROI(targetROI: number | undefined, locale: Locale) {
    const { readOnly } = this.props.proposal;

    const targetROIClasses = classNames('target-rate', 'd-flex', 'align-items-center', {
      'no-print': targetROI === undefined,
    });

    return (
      <div className={targetROIClasses}>
        <label>
          <FormattedMessage id="Charts.Growth.TargetROI" locale={locale} />
        </label>

        <Input className="target-roi" compact type="number" onValueChange={this.onChangeTargetROI} value={targetROI} disabled={readOnly} />

        <label>
          <span className="print-only">{targetROI}</span>%
        </label>
      </div>
    );
  }

  renderGrowth(capital: number | undefined, decimals: number | undefined, locale: Locale, readOnly?: boolean) {
    const { toolbarOptionConfig } = this.props;
    const formattedCapital = formatNumber(capital != null ? capital : DEFAULT_OPTIONS.capital, decimals != null ? decimals : DEFAULT_DECIMALS, locale);

    const customTitle =
      toolbarOptionConfig?.toolbarElementTitles && toolbarOptionConfig.toolbarElementTitles[ProposalNS.ToolbarElement.GrowthOfCapital][locale];

    return (
      <div className="title print flex-grow-1">
        {customTitle || <FormattedMessage id="Proposal.Modules.Toolbar.Growth.Title" locale={locale} />}
        <EditableInput
          type="number"
          compact
          value={capital}
          defaultToEdit={false}
          onBlur={this.onBlurCapitalChange}
          formattedValue={formatMessageWithLocale('Charts.Growth.FormattedCurrency', locale, { capital: formattedCapital })}
          onValueChange={this.onCapitalChange}
          readOnly={readOnly}
        />
      </div>
    );
  }

  handleRangeStartDate = (startDate: Nullable<Date>) => {
    const { commonInception } = this.props.proposal;
    if (startDate) {
      return startDate;
    }

    if (commonInception == null || commonInception.startTimestamp == null) {
      return null;
    }

    return moment.utc(commonInception.startTimestamp).toDate();
  };

  handleQuickRangeValues = (): Array<TIME_PERIODS> => getDateQuickRangeFromToolbarConfig(this.props.toolbarOptionConfig, this.props.proposal);

  render() {
    const { className, toolbarOptionConfig, proposal, toolbarOption, sectionIteratorIndex } = this.props;
    const { initialized } = this.state;
    const {
      options: { locale },
      readOnly,
      toolbarOptions,
    } = proposal;

    const toolbarElements = toolbarOptionConfig !== undefined ? toolbarOptionConfig.toolbarElements || [] : [];
    const useMonthly = toolbarOption !== undefined ? toolbarOption.useMonthly : undefined;
    let startDate = toolbarOption !== undefined ? toolbarOption.startDate : undefined;
    const endDate = toolbarOption !== undefined ? toolbarOption.endDate : undefined;
    const monthsRange = toolbarOption !== undefined ? toolbarOption.monthsRange : undefined;
    const months = startDate ? moment(endDate).diff(startDate, 'months') : null;
    let capital = toolbarOption !== undefined ? toolbarOption.capital : undefined;
    const decimalPlace = toolbarOptionConfig !== undefined ? toolbarOptionConfig.decimalPlace : undefined;
    let targetROI = toolbarOption !== undefined ? toolbarOption.targetROI : undefined;
    const rebalancePeriod = toolbarOption !== undefined ? toolbarOption.rebalancePeriod : undefined;
    const showGrowthOfCapital = toolbarElements.includes(ProposalNS.ToolbarElement.GrowthOfCapital);
    const showRateOfReturn = toolbarElements.includes(ProposalNS.ToolbarElement.RateOfReturn);
    const showDateQuickRange = toolbarElements.includes(ProposalNS.ToolbarElement.DateQuickRange);
    const showDateRangeSelect = toolbarElements.includes(ProposalNS.ToolbarElement.DateRangeSelect);
    const showRebalanceFrequency = toolbarElements.includes(ProposalNS.ToolbarElement.RebalanceFrequency);
    const disableRebalanceFrequency = toolbarOptionConfig !== undefined && toolbarOptionConfig.disableRebalancePeriod;
    const dateQuickRangeValues = this.handleQuickRangeValues();
    const showUseMonthly = toolbarElements.includes(ProposalNS.ToolbarElement.UseMonthlyData) && !!months && months < PERIOD_MONTHS['10Y'] && months >= 6;
    const showDownshift = showGrowthOfCapital || showRebalanceFrequency || showUseMonthly;

    const dateRange = startDate ? moment(endDate).diff(startDate, 'months') : null;
    const dateQuickRange = monthsToPeriodFromValues(dateRange, monthsRange, dateQuickRangeValues);

    if (toolbarOptionConfig != null && sectionIteratorIndex != null) {
      const indexedOptions = toolbarOptions[toolbarOptionConfig.key]?.indexedOptions;
      const sectionIndex = sectionIteratorIndex.toString();
      // override the start date if one is defined in the indexedOptions (used for INC/CI)
      if (indexedOptions != null && indexedOptions[sectionIndex]?.startDate != null) {
        startDate = indexedOptions[sectionIndex]?.startDate;
      }

      // override the capital and target roi if one is defined in the indexedOptions (used for the planning section - Conquest)
      if (indexedOptions != null && indexedOptions[sectionIndex]?.capital != null) {
        capital = indexedOptions[sectionIndex]?.capital;
      }

      if (indexedOptions != null && indexedOptions[sectionIndex]?.targetROI != null) {
        targetROI = indexedOptions[sectionIndex]?.targetROI;
      }
    }

    return (
      <div className={classNames('proposal-toolbar d-flex justify-content-end align-items-center', className)}>
        {showGrowthOfCapital && initialized && this.renderGrowth(capital, decimalPlace, locale, readOnly)}

        {(showDateQuickRange || showRateOfReturn) && (
          <div className={classNames('options-container d-flex align-items-center', { print: !!targetROI })}>
            {showRateOfReturn && initialized && this.renderROI(targetROI, locale)}

            {showDateQuickRange && (
              <div className="no-print">
                <DateQuickRangeOneWay
                  /* readOnly={readOnly} */
                  selected={dateQuickRange}
                  onChange={this.onMonthsRangeChange}
                  locale={locale}
                  values={dateQuickRangeValues}
                  /* endDate={endDate} */
                />
              </div>
            )}
          </div>
        )}

        {showDateRangeSelect && startDate !== undefined && endDate && (
          <DateRangeSelect
            className="print"
            startDate={this.handleRangeStartDate(startDate)}
            endDate={endDate}
            onChange={this.onChangeDate}
            maxDate={getMaxEndDate(proposal.monthEndDataOption)}
            locale={locale}
            readOnly={readOnly}
          />
        )}

        {showDownshift && (
          <Downshift>
            {({ getMenuProps, isOpen, toggleMenu }) => (
              <div className="toolbar-icons d-flex align-items-center">
                <div
                  onClick={() => {
                    if (!readOnly) toggleMenu();
                  }}
                  className="d-flex align-items-center"
                >
                  <i className={classNames('icon-plus toggle-options', { toggled: isOpen })} />
                </div>

                <div className={classNames('advanced-options', { 'opened d-table': isOpen })} {...getMenuProps()}>
                  {showGrowthOfCapital && (
                    <div className="d-table-row">
                      <div className="d-table-cell">
                        <label>
                          <FormattedMessage id="Charts.Growth.InitialCapital" locale={locale} />
                        </label>
                      </div>

                      <div className="d-table-cell">
                        <Input type="number" compact value={capital} onValueChange={this.onCapitalChange} onBlur={this.onBlurCapitalChange} />
                        <label className="unit-symbol">$</label>
                      </div>
                    </div>
                  )}

                  {showRebalanceFrequency && (
                    <div className="d-table-row">
                      <div className="d-table-cell">
                        <label>
                          <FormattedMessage id="Charts.Growth.Rebalance" locale={locale} />
                        </label>
                      </div>

                      <div className="d-table-cell">
                        <SelectInput
                          compact
                          onValueChange={this.onRebalanceChange}
                          selected={rebalancePeriod && rebalancePeriod.value}
                          className="rebalancing-select"
                          disabled={disableRebalanceFrequency}
                        >
                          {this.getRebalancingOptions().map(opt => (
                            <SelectOption key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectOption>
                          ))}
                        </SelectInput>
                      </div>
                    </div>
                  )}

                  {showUseMonthly && (
                    <div className="d-table-row monthly-data">
                      <div className="d-table-cell">
                        <label id={this.useMonthlyLabelId}>
                          <FormattedMessage id="Charts.UseMonthlyData.Title" locale={locale} />
                          <InfoTooltip message={formatMessageWithLocale('Charts.UseMonthlyData.Disclaimer', locale)} />
                          {'\xa0'}:
                        </label>
                      </div>

                      <div className="d-table-cell">
                        <Checkbox ariaLabelledby={this.useMonthlyLabelId} selected={useMonthly} onChange={this.onIntervalChange} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Downshift>
        )}
      </div>
    );
  }
}

export type ProposalToolbarProps = Props;

export default ProposalToolbar;
